import { FederatedAuthServiceImpl } from "~/backend/generated/FederatedAuth/api/federatedAuth.service";
import { FederatedAuthService } from "~/backend/generated/FederatedAuth/api/federatedAuth.serviceInterface";
import { ContextManager, ContextInstance } from "~/modules/contextManager/ContextManager";
import { ClientOptionsStore } from "~/modules/client/ClientOptions/ClientOptionsStore";
import { InternalError } from "~/modules/error";
import { getLogger, Logger, LoggerName } from "~/modules/logger";
import { assertNotEmptyString } from "~/utils/assert";
import type { AuthenticatorDataContainer } from "../AuthenticatorDataContainer/AuthenticatorDataContainer";
import { AuthenticatorDataContainerImpl } from "../AuthenticatorDataContainer/AuthenticatorDataContainerImpl";
import { getSSOLoginRequestBody } from "../AuthenticatorHelpers/getSSOLoginRequestBody";
import type { Authenticator, IdpConfig, TokenData, TokenRefreshResult } from "./Authenticator";

export class AuthenticatorImpl implements Authenticator {
    readonly #authenticatorDataContainer: AuthenticatorDataContainer;

    readonly #logger: Logger;

    readonly #federatedAuthService: FederatedAuthService;

    readonly #clientOptions: ClientOptionsStore;

    constructor(ctx: ContextManager) {
        this.#logger = getLogger(ctx)(LoggerName.Auth);
        this.#authenticatorDataContainer = ctx.retrieveInstanceOf(
            ContextInstance.AuthenticatorDataContainerImpl,
            AuthenticatorDataContainerImpl
        );
        this.#clientOptions = ctx.retrieveInstanceOf(ContextInstance.ClientOptionsStore, ClientOptionsStore);
        this.#federatedAuthService = ctx.retrieveInstanceOf(
            ContextInstance.FederatedAuthServiceImpl,
            FederatedAuthServiceImpl
        );
    }

    async getIdpUrl(config: IdpConfig): Promise<string> {
        assertNotEmptyString(config.redirectUrl, "redirect url");

        const payload = getSSOLoginRequestBody(config);
        const accountSid = this.#authenticatorDataContainer.accountSid;

        const data = await this.#federatedAuthService.getIdpUrl(accountSid, payload);
        if (!data.location) {
            this.#logger.error("No redirect location from /authenticate request, data: ", data);
            throw new InternalError("Invalid response from /authenticate endpoint");
        }
        return data.location;
    }

    async validateToken(token: string): Promise<TokenData> {
        const accountSid = this.#authenticatorDataContainer.accountSid;
        const tokenData = await this.#federatedAuthService.validateToken(accountSid, { token }, { token });

        return {
            roles: tokenData.roles,
            valid: tokenData.valid,
            dateExpired: tokenData.expiration,
            identity: tokenData.identity,
            flexUserSid: tokenData.flexUserSid
        };
    }

    async refreshToken(token: string): Promise<TokenRefreshResult> {
        const accountSid = this.#authenticatorDataContainer.accountSid;
        const tokenRefreshResult = await this.#federatedAuthService.refreshToken(accountSid, {
            token
        });
        return { token: tokenRefreshResult.token, dateExpired: tokenRefreshResult.expiration };
    }
}
